/**
 * Reopen Button Component
 * Displays a floating button to reopen cookie preferences
 */

import type { ConsentManager } from "./consent-manager";
import type {
	PluginConfig,
	ReopenButtonPosition,
	ReopenButtonStyle,
} from "./config";

export class ReopenButton {
	private manager: ConsentManager;
	private config: PluginConfig;
	private button: HTMLButtonElement | null = null;
	private isVisible = false;

	constructor(manager: ConsentManager, config: PluginConfig) {
		this.manager = manager;
		this.config = config;
	}

	/**
	 * Create and insert the reopen button into the DOM
	 */
	public create(): void {
		if (this.button) return;

		const reopenConfig = this.config.appearance.reopenButton;
		if (!reopenConfig?.enabled) return;

		const button = document.createElement("button");
		button.type = "button";
		button.id = "dcbl-cc-reopen-btn";
		button.className = this.buildClassName(
			reopenConfig.position,
			reopenConfig.style,
		);
		button.setAttribute("aria-label", this.t("reopen_btn_aria"));
		button.setAttribute("aria-hidden", "true");

		// Build button content based on style
		button.innerHTML = this.buildContent(reopenConfig.style);

		// Event handler
		button.addEventListener("click", () => {
			this.manager.showSettings();
		});

		document.body.appendChild(button);
		this.button = button;
	}

	/**
	 * Show the reopen button
	 */
	public show(): void {
		if (!this.button || this.isVisible) return;

		this.button.classList.add("dcbl-cc-reopen-btn--visible");
		this.button.setAttribute("aria-hidden", "false");
		this.isVisible = true;
	}

	/**
	 * Hide the reopen button
	 */
	public hide(): void {
		if (!this.button || !this.isVisible) return;

		this.button.classList.remove("dcbl-cc-reopen-btn--visible");
		this.button.setAttribute("aria-hidden", "true");
		this.isVisible = false;
	}

	/**
	 * Check if button is currently visible
	 */
	public getIsVisible(): boolean {
		return this.isVisible;
	}

	/**
	 * Focus the button (for accessibility after closing modal)
	 */
	public focus(): void {
		this.button?.focus();
	}

	private buildClassName(
		position: ReopenButtonPosition,
		style: ReopenButtonStyle,
	): string {
		const classes = [
			"dcbl-cc-reopen-btn",
			`dcbl-cc-reopen-btn--${position}`,
			`dcbl-cc-reopen-btn--${style}`,
		];
		return classes.join(" ");
	}

	private buildContent(style: ReopenButtonStyle): string {
		const gearIcon = `<svg class="dcbl-cc-reopen-btn__icon" viewBox="0 0 24 24" aria-hidden="true">
			<path d="M19.14 12.94c.04-.31.06-.63.06-.94 0-.31-.02-.63-.06-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.04.31-.06.63-.06.94s.02.63.06.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z" fill="currentColor"/>
		</svg>`;

		const text = `<span class="dcbl-cc-reopen-btn__text">${this.escapeHtml(this.t("reopen_btn"))}</span>`;

		switch (style) {
			case "icon":
				return gearIcon;
			case "text":
				return text;
			case "icon-text":
				return gearIcon + text;
			default:
				return gearIcon;
		}
	}

	private t(key: string): string {
		return this.manager.getTranslation(key);
	}

	private escapeHtml(str: string): string {
		const div = document.createElement("div");
		div.textContent = str;
		return div.innerHTML;
	}
}
